import React, {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Box,
  Button,
  FormControl,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Select,
  InputLabel,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  CircularProgress, Tooltip, IconButton
} from "@mui/material";
import Resizer from 'react-image-file-resizer';
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoIcon from "@mui/icons-material/Info";
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import Rotate90DegreesCwIcon from '@mui/icons-material/Rotate90DegreesCw';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import Jimp from "jimp";
import "../../App.css";
import '../../css/scan-popup.css';
import { useUser } from "../../UserContext";
import { AppHelper } from "../../AppHelper";

const ScanPopup = forwardRef(
  (
    { isOpen, isCropped, onClose, onScanComplete, onCamOpen, isSa, isClear, scanAgain },
    ref
  ) => {
    const [selectedVersion, setSelectedVersion] = useState("2.1");
    const MAX_PAGES = 5;

    const [pages, setPages] = useState([
      { b64: null, align: false, order: 0, rotation: 0 },
    ]);
    const fileInputRef = useRef(null);
    const [uploadFiles, setUploadFiles] = useState([]);
    const [fileIndex, setFileIndex] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isEnhancedTranscription, setIsEnhancedTranscription] = useState(true);
    const [imagePreview, setImagePreview] = React.useState({ open: false, src: '', alt: '' });
    const [maxPageReach, setMaxPageReach] = useState(false);
    useEffect(()=>{
    }, [uploadFiles]);
    useEffect(()=>{
      const filledPages = pages.filter(p => p.b64 !== null).length;
      if (filledPages >= MAX_PAGES) {
        setMaxPageReach(true);
      } else {
        setMaxPageReach(false);
      }
    }, [pages]);
    useEffect(()=>{
    }, [fileIndex]);

    const handleAdd = (argIndex)=>{
      const filledPages = pages.filter(p => p.b64 !== null).length;
      if (filledPages >= MAX_PAGES) {
        alert(`You can only add up to ${MAX_PAGES} pages.`);
        return;
      }
      setPages([...pages, { b64: null, align: false }]);
    }
    const handleBrowse = (argIndex)=>{
      fileInputRef.current.click();
    }
    const handleClose = () => {
      onClose();
    };
    const handleFileChange = async (event) => {
      if(!event.target.files || event.target.files.length<1) return;
      const filledPages = pages.filter(p => p.b64 !== null).length;
      let remainingSlots = MAX_PAGES - filledPages;
      if (remainingSlots <= 0) {
        alert(`You already have ${MAX_PAGES} pages. Please remove one before adding more.`);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      var _files = [...event.target.files];
      _files = AppHelper.sortList(_files, 'name');
      if (_files.length > remainingSlots) {
        alert(`You can only upload ${remainingSlots} more page(s). Extra images will be ignored.`);
        _files = _files.slice(0, remainingSlots);
      }
      setUploadFiles(_files);
      if (fileInputRef.current) fileInputRef.current.value = ""; // reset file control
      var _pages = [...pages];
      var _startIndex = _pages.length-1;
      for(var i=0; i<_files.length; i++){
        if(_pages.length<i+1) _pages = [..._pages, {b64: null, align: false, order:_startIndex+i}];
        var _page = _pages[_startIndex+i];
        var _pageData = await pageDataRead(_files[i]);
        _page = {..._page, ..._pageData};
        _pages[_startIndex+i] = _page;
      }
      setPages(_pages);
    };
    const pageDataRead = async(argFile)=>{
      try {
        const reader = new FileReader();
        reader.readAsDataURL(argFile);
        const isHeicFile = argFile.name.toLowerCase().endsWith('.heic');
        if (isCropped) {//-- disabled crop fun for now (original code in scan-popup-v1)
        }
        else{
          await new Promise((resolveReader, rejectReader) => {
            reader.onload = () => {
              resolveReader();
            };
            reader.onerror = (error) => {
              rejectReader(error);
            };
          })
          const base64 = reader.result.split(",")[1];
          return { b64: base64, fileType:argFile.type, fileSize:argFile.size, isHeicFile:isHeicFile, align:false };
        }
      }
      catch (error) {
        console.error("Error converting image to base64:", error);
        return null;
      }
    }
    const handleRemove = (argIndex)=>{
      setPages((prevPages) => {
        // If there's only 1 page left, reset it to empty instead of removing
        if (prevPages.length === 1) {
          return [{ b64: null, align: false, order: 0, rotation: 0 }];
        }
        // Otherwise, remove the selected page
        const newPages = [...prevPages];
        newPages.splice(argIndex, 1);
        return newPages;
      });
    }
    const handleRetake = async(argIndex)=>{
      var _pages = [...pages];
      _pages[argIndex].b64 = null;
      setPages(_pages);
    }
    const orderDown = (argIndex)=>{
      if(argIndex == pages.length-1) return;
      var _pages = [...pages];
      var _rec = {...pages[argIndex]};
      var _recDown = {...pages[argIndex+1]};
      _pages[argIndex] = _recDown;
      _pages[argIndex+1] = _rec;
      setPages(_pages);
    }
    const orderUp = (argIndex)=>{
      if(argIndex == 0) return;
      var _pages = [...pages];
      var _rec = {...pages[argIndex]};
      var _recUp = {...pages[argIndex-1]};
      _pages[argIndex] = _recUp;
      _pages[argIndex-1] = _rec;
      setPages(_pages);
    }

    const handleRotate = async (index) => {
      const page = pages[index];
      if (!page.b64) {
        return;
      }
      setIsLoading(true); 
      const image = new Image();
      image.src = `data:image/jpeg;base64,${page.b64}`;
    
      try {
        await new Promise((resolve, reject) => {
          image.onload = () => resolve();
          image.onerror = reject;
        });
    
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
    
        canvas.width = image.height;
        canvas.height = image.width;
    
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(Math.PI / 2); // 90 degrees
        ctx.drawImage(image, -image.width / 2, -image.height / 2);
    
        const rotatedBase64 = canvas.toDataURL('image/jpeg');
        const updatedPages = pages.map((p, i) => {
          if (i === index) {
            return {
              ...p,
              b64: rotatedBase64.split(",")[1],
            };
          }
          return p;
        });
        setPages(updatedPages);
        setIsLoading(false);
      } catch (error) {
        console.error('Error rotating image:', error);
        setIsLoading(false);
      }
    };
    
    const handleScan = async (argEvent, argVer) => {
      const isValid = pages.every((page) => page.b64 !== null);
      if (!isValid) {
        alert("Please upload or capture images for all pages.");
        setIsLoading(false); // Set loading state back to false
        return;
      }
      const totalSize = pages.reduce((acc, page) => {
        const base64String = page.align
          ? page.alignB64
          : page.b64;
        const sizeB = new Buffer(base64String, "base64").length;
        const sizeMB = (sizeB / (1024 * 1024)).toFixed(2);
        return acc + sizeMB;
      }, 0);
      if (totalSize > 30) {
        alert(
          "Total image size exceeds 30 MB. Please reduce the number or size of images."
        );
        setIsLoading(false); // Set loading state back to false
        return;
      }

      setIsLoading(true); // Set loading state to true
      const images = pages.map((page) =>
        page.align ? page.alignB64 : page.b64
      ); // Get all imageBase64 values

      for(var i=0;i<images.length;i++){
        const imgVW2 = new Image();
        imgVW2.src = `data:image/jpeg;base64,${images[i]}`;

        // Wrap onload event in a Promise
        await new Promise((resolve, reject) => {
          imgVW2.onload = () => {
            resolve();
          };
          imgVW2.onerror = (error) => {
            reject(error);
          };
        });

        // Once the image has loaded, you can access the dimensions
        const imgVW2_w = imgVW2.naturalWidth;
        const imgVW2_h = imgVW2.naturalHeight;
        const mp = (imgVW2_w * imgVW2_h) / 2000000;  // Calculate megapixels
        const max_mp = 1.5;//2
        if(mp>max_mp){
          const scale = Math.sqrt(max_mp / mp);
          const new_height = parseInt(imgVW2_h * scale);
          const new_width = parseInt(imgVW2_w * scale);
          const imgFileOrigin = AppHelper.imgB64ToFile(images[i]);
          const resizedFile = await new Promise((resolve, reject) => {
            Resizer.imageFileResizer(
              imgFileOrigin,
              new_width,
              new_height,
              'JPEG', // Output format
              99, // Quality
              0, // Rotation
              (resizedFile) => {
                resolve(resizedFile);
              },
              'blob' // Output type
            );
          });
          images[i] = await AppHelper.imgFileToB64(resizedFile);
        }
      }

      var _url = "api/essay/scan";
      if (selectedVersion == "2.0") _url = "api/essay/scan/2.0";
      else if (selectedVersion == "2.1") _url = "api/essay/scan/2.1";

      if(isEnhancedTranscription) argVer = '4.0';
      if(argVer) _url = "api/essay/scan/" + argVer;

      var _res = await AppHelper.apiPost(_url, { token: "", imgs: images });

      if (!_res || !_res.status) {
        alert("ERROR: Failed to scan!");
        setIsLoading(false); // Set loading state back to false
        return;
      } else {
        setIsLoading(false); // Set loading state back to false
        onScanComplete(_res, images, isEnhancedTranscription);
        onClose();
      }
    };

    const openImagePreview = (b64, alt='') => {
      setImagePreview({ open: true, src: `data:image/png;base64,${b64}`, alt });
    };
    const closeImagePreview = () => setImagePreview({ open: false, src: '', alt: '' });

    return (
      <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="xs">
        <DialogTitle sx={{fontWeight: "bold"}}>Scan Image</DialogTitle>
        <DialogTitle sx={{ marginTop: '-2rem', fontSize: '1rem'}}>Upload clear photos of each page to scan the essay. </DialogTitle>
        <DialogContent>
          <Grid container direction="column" spacing={4}>
            {pages.map((p, iPage) => (
              <>
              <Grid item key={'scan_img_' + iPage}>
                {!p.b64 && (
                  <Grid
                  container
                  // direction="column"
                  position="relative"
                  justifyContent="center"
                  alignItems="center"
                  className={p.b64? '':'scan-upload-box'}
                  sx={{ width: "50%", mx: "auto", textAlign: "center"}}>
                    {/* Close button: only allow close upload btn when have at least 1 page added.  */}
                    {!p.b64 && iPage === pages.length - 1 && pages.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemove(iPage)}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          color: "grey.600",
                          backgroundColor: "white",
                          "&:hover": {
                            backgroundColor: "grey.100",
                          },
                          zIndex: 10
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    )}
                    <>
                      <Button
                        startIcon={<CloudUploadIcon />}
                        sx={{ marginBottom: "8px" }}
                        onClick={() => handleBrowse(iPage)}
                      >
                        Upload
                      </Button>
                    </>
                  </Grid>
                )}
                {p.b64 && (
                <Grid container spacing={2} alignItems="flex-start">
                  {/* LEFT: Image/up down arrow.  */}
                  <Grid item xs={5}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "left",
                        // gap: 1,
                      }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            // gap: 1,
                            height: "100%",
                            minHeight: "120px",
                            position: "relative"
                          }}
                        >
                          {p.b64 && iPage > 0 && (
                            <Tooltip title="Move page up" arrow>
                            <IconButton sx={{ p: 0, position: "absolute", top: 0, color: "primary.main" }} onClick={() => orderUp(iPage)}>
                              <ArrowCircleUpIcon sx={{ fontSize: 32 }} />
                            </IconButton>
                            </Tooltip>
                          )}
                          {iPage + 1 < pages.length && pages[iPage + 1]?.b64 &&  (
                            <Tooltip title="Move page down" arrow>
                            <IconButton sx={{ p: 0, position: "absolute", bottom: 0, color: "primary.main" }} onClick={() => orderDown(iPage)}>
                              <ArrowCircleDownIcon sx={{ fontSize: 32 }} />
                            </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        { p.b64 && !p.alignLoading && (
                          <Box sx={{ position: "relative", display: "inline-block", marginLeft: "1rem" }}>
                            {/* Cancel / Remove Page button */}
                            <IconButton
                              onClick={() => handleRemove(iPage)}
                              style={{
                                width: "25px", 
                                height: "25px",
                                position: 'absolute',
                                top: -5, 
                                right: -5, 
                                color: 'rgba(255, 255, 255, 0.8)', 
                                backgroundColor: '#1976d2', 
                                margin: '5px', 
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                },
                              }}>
                                <CloseIcon />
                            </IconButton>
                            <Tooltip title="Click to view full image" arrow>
                            <IconButton
                              onClick={() => openImagePreview(p.b64)}
                              sx={{
                                position: 'absolute',
                                top: "50%", 
                                left: "50%", 
                                transform: "translate(-50%, -50%)", 
                                color: 'rgba(255, 255, 255, 0.8)', 
                                backgroundColor: 'rgba(0, 0, 0, 0.5)', 
                                marginLeft: '0.5rem', 
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                },
                              }}>
                                <OpenInFullIcon />
                            </IconButton>
                            </Tooltip>
                          <Box component="img"
                              src={`data:image/jpeg;base64,${ p.align ? p.alignB64 : p.b64 }`}
                              alt={`Page ${iPage + 1}`}
                              sx={{ width: 100, borderRadius: 1, objectFit: "cover", marginLeft: "1rem" }}
                              onClick={(e) => {
                                openImagePreview(p.b64);
                              }}
                          />
                        </Box>
                        )}
                      </Box>

                  </Grid>
                  <Grid item xs={7}>
                    <Grid
                      container
                      direction="column"
                      justifyContent="left"
                      // className={p.b64? '':'scan-upload-box'}
                      sx={{ width: "100%", textAlign: "center" }}
                    >
                      {p.b64 && 
                      <>
                        <div
                            style={{
                              textAlign: "left",
                              marginTop: "-2px",
                              marginBottom: "8px",
                            }}
                          >{`Page ${iPage + 1}`}
                        </div>
                        <FormControlLabel
                          style={{marginBottom: "1rem"}}
                          control={
                            <Rotate90DegreesCwIcon
                              color="primary"
                              sx={{marginRight: '6px'}}
                            />
                          }
                          onClick={() => handleRotate(iPage)}
                          label={'Rotate Clockwise'}
                          sx={{marginLeft: '0px', '& .MuiFormControlLabel-label': { color: "primary.main" }}}
                        />

                        { iPage == pages.length-1 &&
                          <>
                          <FormControlLabel
                            style={{marginBottom: "1rem"}}
                            control={
                              <RefreshIcon
                                color="primary"
                                sx={{marginRight: '6px'}}
                              />
                            }
                            onClick={() => handleRetake(iPage)}
                            label={'Retake'}
                          sx={{marginLeft: '0px', '& .MuiFormControlLabel-label': { color: "primary.main" }}}
                          />
                          </>
                        }
                      </>
                      }
                    </Grid>
                  </Grid>
                </Grid>
                )}
              </Grid>
            { iPage == pages.length-1 && p.b64 && !maxPageReach && 
            <Grid item>
              <Grid container justifyContent="center">
                <Button
                  onClick={handleAdd}
                  variant="outlined"
                  sx={{ 
                    color: 'rgba(19,130,117,1)',
                    borderColor: 'rgba(19,130,117,1)',
                    '&:hover': {
                      backgroundColor: 'rgba(19,130,117,0.08)',
                      borderColor: 'rgba(19,130,117,1)',
                    },
                    // marginTop: "-1.5rem"
                  }}
                  startIcon={<AddIcon />}
                >
                  <span style={{ marginBottom: "-1px" }}>
                    Add Page
                  </span>
                </Button>
              </Grid>
            </Grid>
            }
            { ( p.b64 || iPage != 0 ) && iPage == pages.length-1 &&
            <Grid item>
              <Grid container justifyContent="center">
                <Button
                  className="app-btn-bg"
                  color="info"
                  variant="contained"
                  onClick={handleScan}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "SCAN"
                  )}
                </Button>
              </Grid>
            </Grid>
            }
            </>
            ))}
          </Grid>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/bmp/heic/HEIC"
            style={{ display: "none" }}
            onChange={handleFileChange}
            multiple='multiple'
          />
        </DialogContent>
        <Button
          onClick={handleClose}
          style={{ position: "absolute", top: 0, right: 0 }}
        >
          <CloseIcon />
        </Button>
        <Dialog open={imagePreview.open} onClose={closeImagePreview} maxWidth="md" fullWidth>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', position: "relative" }}>
            <IconButton
            onClick={closeImagePreview}
            style={{
              width: "25px", 
              height: "25px",
              position: 'absolute',
              top: "2rem", 
              right: "1rem", 
              color: 'rgba(255, 255, 255, 0.8)', 
              backgroundColor: 'rgba(0, 0, 0, 0.6)', 
              margin: '5px', 
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
              },
            }}>
              <CloseIcon />
            </IconButton>
              <img
              src={imagePreview.src}
              alt={imagePreview.alt || 'Preview'}
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </Box>
        </Dialog>
      </Dialog>
      
    );
  }
);

export default ScanPopup;
